'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var SessionContext = require('../../../../../../common/providers/SessionContext.js');
var nthHookLoma = require('@narvar/nth-hook-loma');
var index = require('../../../../../../common/providers/ClaimBuilder/index.js');
var reactRouterDom = require('react-router-dom');
var paths = require('../../../../../../paths.js');
var useTitanAnalytics = require('../../../../../../common/providers/titan/useTitanAnalytics.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const OTPContext = /*#__PURE__*/React__default["default"].createContext(null);
const useOTP = () => {
  const context = React.useContext(OTPContext);
  if (!context) {
    throw new Error('No context provided for OTP provider');
  }
  return context;
};
const OTPProvider = _ref => {
  var _otpSendStatus$data, _otpSendStatus$data$o, _otpVerifyStatus$data, _otpVerifyStatus$data2;
  let {
    children
  } = _ref;
  const titan = useTitanAnalytics["default"]();
  const {
    handleError
  } = SessionContext.useSession();
  const {
    pathname
  } = reactRouterDom.useLocation();
  const navigate = reactRouterDom.useNavigate();
  const {
    retailerMoniker,
    localeCountry,
    localeLanguage,
    orderNumber
  } = SessionContext.useSession();
  const {
    state: {
      claimEligibilityInfo
    },
    handleOtpStepComplete
  } = index.useClaimBuilder();
  const {
    encryptedPhone,
    last4Phone: encryptedLast4Phone
  } = claimEligibilityInfo || {};
  const [codeRequestsForOTPPhone, setCodeRequestsForPhone] = React.useState({});
  const [otpSendMutation, otpSendStatus] = nthHookLoma.useOtpSendMutation();
  const [otpVerifyMutation, otpVerifyStatus] = nthHookLoma.useOtpVerifyMutation();

  // The manually entered phone number
  const [phone, setPhone] = React.useState('');

  // This keeps track of the phone which is currently being used for OTP.
  // This is either the manually entered phone if set, else the phone in the order.
  // Requests to the OTP apis require either a phone or an encrypted phone, and specifying
  // that type in requests.
  const currentPhone = phone ? {
    type: 'PHONE',
    value: phone
  } : {
    type: 'ENCRYPTED_PHONE',
    value: encryptedPhone
  };
  const codeRequestsForCurrentPhone = currentPhone.value ? codeRequestsForOTPPhone[currentPhone.value] : null;
  const otpSkipReasons = [{
    value: 'NO_MOBILE_PHONE',
    text: `I don't have a mobile phone`
  }, {
    value: 'NOT_NEAR_PHONE',
    text: 'My phone is not with me'
  }, {
    value: 'UNABLE_TO_RECEIVE_MESSAGES',
    text: `I can't receive messages`
  }, {
    value: 'DID_NOT_RECEIVE_CODE',
    text: `I didn't receive a code`
  }];
  const incrementCodeRequestsForPhone = phone => {
    setCodeRequestsForPhone(prev => {
      var _prev$phone;
      const count = (((_prev$phone = prev[phone]) === null || _prev$phone === void 0 ? void 0 : _prev$phone.count) || 0) + 1;
      const maxRequestsReached = count >= 3;
      return {
        ...prev,
        [phone]: {
          count,
          maxRequestsReached
        }
      };
    });
  };
  const otpSendToNewPhone = async phone => {
    var _result$data, _result$data$otpSend;
    const result = await otpSendMutation({
      variables: {
        retailerMoniker,
        localeCountry,
        localeLanguage,
        orderNumber,
        encryptedPhone: null,
        phone
      }
    });
    if ((_result$data = result.data) !== null && _result$data !== void 0 && (_result$data$otpSend = _result$data.otpSend) !== null && _result$data$otpSend !== void 0 && _result$data$otpSend.success) {
      titan === null || titan === void 0 ? void 0 : titan.track({
        event: 'promotion_clicked',
        properties: {
          promotion_id: 'otp',
          variant: 'code_sent_new_phone'
        }
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
      setPhone(phone);
      incrementCodeRequestsForPhone(phone);
      otpSendStatus.reset();
      navigate(paths.ORDER_LOOKUP_OTP_VERIFY);
    } else {
      handleError('Error sending otp code to new phone');
    }
  };
  const otpSendToOrderPhone = async () => {
    var _result$data2, _result$data2$otpSend;
    if (!encryptedPhone) {
      throw new Error('Encrypted phone number is required to send OTP to order phone');
    }
    const result = await otpSendMutation({
      variables: {
        retailerMoniker,
        localeCountry,
        localeLanguage,
        orderNumber,
        encryptedPhone,
        phone: null
      }
    });
    if ((_result$data2 = result.data) !== null && _result$data2 !== void 0 && (_result$data2$otpSend = _result$data2.otpSend) !== null && _result$data2$otpSend !== void 0 && _result$data2$otpSend.success) {
      titan === null || titan === void 0 ? void 0 : titan.track({
        event: 'promotion_clicked',
        properties: {
          promotion_id: 'otp',
          variant: 'code_sent_order_phone'
        }
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
      incrementCodeRequestsForPhone(encryptedPhone);
      otpSendStatus.reset();
      navigate(paths.ORDER_LOOKUP_OTP_VERIFY);
    } else {
      handleError('Error sending otp code to order phone');
    }
  };
  const otpResend = async () => {
    var _result$data3, _result$data3$otpSend;
    const selectedPhone = phone || encryptedPhone;
    if (!selectedPhone) throw new Error('No phone number to resend OTP to');
    const phoneParam = currentPhone.type === 'PHONE' ? {
      phone
    } : {
      encryptedPhone
    };
    const result = await otpSendMutation({
      variables: {
        retailerMoniker,
        localeCountry,
        localeLanguage,
        orderNumber,
        ...phoneParam
      }
    });
    if ((_result$data3 = result.data) !== null && _result$data3 !== void 0 && (_result$data3$otpSend = _result$data3.otpSend) !== null && _result$data3$otpSend !== void 0 && _result$data3$otpSend.success) {
      titan === null || titan === void 0 ? void 0 : titan.track({
        event: 'promotion_clicked',
        properties: {
          promotion_id: 'otp',
          variant: 'code_resent'
        }
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
      incrementCodeRequestsForPhone(selectedPhone);
    } else {
      handleError('Error resending otp');
    }
  };
  const otpVerify = async code => {
    var _result$data4;
    const phoneParam = currentPhone.type === 'PHONE' ? {
      phone
    } : {
      encryptedPhone
    };
    const result = await otpVerifyMutation({
      variables: {
        retailerMoniker,
        code,
        ...phoneParam
      }
    });
    const {
      success,
      otpJwt
    } = ((_result$data4 = result.data) === null || _result$data4 === void 0 ? void 0 : _result$data4.otpVerify) || {};
    if (success && otpJwt) {
      titan === null || titan === void 0 ? void 0 : titan.track({
        event: 'promotion_clicked',
        properties: {
          promotion_id: 'otp',
          variant: 'complete_verified',
          extra_properties: [{
            key: 'otp_codes_sent',
            value: codeRequestsForCurrentPhone === null || codeRequestsForCurrentPhone === void 0 ? void 0 : codeRequestsForCurrentPhone.count.toString()
          }]
        }
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
      handleOtpStepComplete({
        skipped: false,
        jwt: otpJwt
      });
    } else {
      handleError('Error verifying otp code');
    }
  };
  const otpSkip = skipReasonValue => {
    var _otpSkipReasons$find;
    const skipReason = (_otpSkipReasons$find = otpSkipReasons.find(reason => reason.value === skipReasonValue)) === null || _otpSkipReasons$find === void 0 ? void 0 : _otpSkipReasons$find.text;
    titan === null || titan === void 0 ? void 0 : titan.track({
      event: 'promotion_clicked',
      properties: {
        promotion_id: 'otp',
        variant: 'complete_skipped',
        extra_properties: [{
          key: 'otp_skip_reason',
          value: skipReason
        }]
      }
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
    handleOtpStepComplete({
      skipped: true,
      skipReason
    });
  };
  const handleNavigateBack = () => {
    if (pathname === paths.ORDER_LOOKUP_OTP_VERIFY) {
      setPhone('');
      otpSendStatus.reset();
      otpVerifyStatus.reset();
    } else if (pathname === paths.ORDER_LOOKUP_OTP_SEND_TO_NEW_PHONE) {
      otpSendStatus.reset();
    } else if (pathname === paths.ORDER_LOOKUP_OTP_SEND_TO_ORDER_PHONE) {
      otpSendStatus.reset();
    }
    navigate(-1);
  };
  const handleUpdateNumberNavigate = () => {
    setPhone('');
    otpSendStatus.reset();
    navigate(paths.ORDER_LOOKUP_OTP_SEND_TO_NEW_PHONE);
  };
  const handleUpdateNumberNavigateFromVerify = () => {
    setPhone('');
    otpVerifyStatus.reset();
    otpSendStatus.reset();
    navigate(paths.ORDER_LOOKUP_OTP_SEND_TO_NEW_PHONE, {
      replace: true
    });
  };
  const handleSkipOtpNavigate = () => {
    navigate(paths.ORDER_LOOKUP_OTP_SKIP);
  };
  return /*#__PURE__*/React__default["default"].createElement(OTPContext.Provider, {
    value: {
      otpSendToOrderPhone,
      otpSendToNewPhone,
      otpResend,
      otpVerify,
      otpSkip,
      otpSkipReasons,
      resetCodeRequestsForPhone: () => setCodeRequestsForPhone({}),
      handleNavigateBack,
      handleUpdateNumberNavigate,
      handleUpdateNumberNavigateFromVerify,
      handleSkipOtpNavigate,
      state: {
        otpSendError: !!otpSendStatus.error || (otpSendStatus === null || otpSendStatus === void 0 ? void 0 : (_otpSendStatus$data = otpSendStatus.data) === null || _otpSendStatus$data === void 0 ? void 0 : (_otpSendStatus$data$o = _otpSendStatus$data.otpSend) === null || _otpSendStatus$data$o === void 0 ? void 0 : _otpSendStatus$data$o.success) === false,
        otpSendLoading: !!otpSendStatus.loading,
        otpSendCalled: !!otpSendStatus.called,
        otpVerifyError: !!otpVerifyStatus.error || (otpVerifyStatus === null || otpVerifyStatus === void 0 ? void 0 : (_otpVerifyStatus$data = otpVerifyStatus.data) === null || _otpVerifyStatus$data === void 0 ? void 0 : (_otpVerifyStatus$data2 = _otpVerifyStatus$data.otpVerify) === null || _otpVerifyStatus$data2 === void 0 ? void 0 : _otpVerifyStatus$data2.success) === false,
        otpVerifyLoading: !!otpVerifyStatus.loading,
        last4Phone: phone ? phone === null || phone === void 0 ? void 0 : phone.slice(-4) : encryptedLast4Phone,
        maxRequestsReachedForCurrentPhone: (codeRequestsForCurrentPhone === null || codeRequestsForCurrentPhone === void 0 ? void 0 : codeRequestsForCurrentPhone.maxRequestsReached) ?? false
      }
    }
  }, children);
};

exports.OTPContext = OTPContext;
exports.OTPProvider = OTPProvider;
exports.useOTP = useOTP;
